import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import { Column, Field, Control, Label, Input, Button, Icon } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import HorizontalRule from "../../../../styles/HorizontalRule";
import VerifyRouteButton from "../../../../components/VerifyRouteButton";

import {
  DriverSelect,
  TractorSelect,
  TrailerSelect,
  BooleanInput,
  DateTimePicker
} from "../../../../components/inputs";

import { VerifiedBy } from "../../../../components/Orders";

const getNetWeightStyle = inputs => {
  const styles = {};
  if (inputs.GrossWeight && inputs.TareWeight && inputs.NetWeight) {
    const calculatedNetWeight =
      parseInt(inputs.GrossWeight, 10) - parseInt(inputs.TareWeight, 10);
    if (calculatedNetWeight !== parseInt(inputs.NetWeight, 10)) {
      styles.color = "red";
      styles.fontWeight = "bolder";
    }
  }
  return styles;
};
const BillOfLading = ({ inputs, editing, disabled, onChange }) => (
  <div>
    <Column.Group>
      <Column size={6}>
        <Field kind="group">
          <Control expanded>
            <Label>Max Gross</Label>
            <Input
              disabled={!editing}
              name="MaxGross"
              type="number"
              value={inputs.MaxGross}
              onChange={onChange}
            />
          </Control>
          <Control expanded>
            <Label>Gross Weight</Label>
            <Input
              disabled={!editing}
              name="GrossWeight"
              type="number"
              value={inputs.GrossWeight}
              onChange={onChange}
            />
          </Control>
          <Control expanded>
            <Label>Tare Weight</Label>
            <Input
              disabled={!editing}
              name="TareWeight"
              type="number"
              value={inputs.TareWeight}
              onChange={onChange}
            />
          </Control>
          <Control expanded>
            <Label>Net Weight</Label>
            <Input
              readOnly
              name="NetWeight"
              style={getNetWeightStyle(inputs)}
              type="number"
              value={inputs.NetWeight}
            />
          </Control>
        </Field>
      </Column>
      <Column>
        <Field kind="group">
          <Control expanded>
            <Label>Order Notes</Label>
            <Input
              disabled={!editing}
              name="OrderNotes"
              type="text"
              value={inputs.OrderNotes}
              onChange={onChange}
            />
          </Control>
          <Control expanded>
            <Label>Last Clock Out Time</Label>
            <DateTimePicker
              disabled={disabled}
              name="LastClockOutTimeFormatted"
              value={inputs.LastClockOutTimeFormatted}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Column>
    </Column.Group>
    <Column.Group>
      {/* PICKUP COLUMN */}
      <Column>
        <Field>
          <Control expanded>
            <Label>Pickup Date/Time</Label>
            <DateTimePicker
              disabled
              readOnly
              name="PickupDate"
              value={inputs.PickupDateFormatted}
            />
          </Control>
        </Field>
        <Field>
          <Control>
            <Label>Pickup Driver</Label>
            <DriverSelect
              icon="user"
              name="PickupDriverRecIdRef"
              readOnly={!editing}
              value={inputs.PickupDriverRecIdRef}
              onChange={onChange}
            />
          </Control>
        </Field>
        <Field kind="group">
          <Control expanded>
            <Label>Pickup Tractor</Label>
            <TractorSelect
              icon="truck"
              name="PickupTractorRecIdRef"
              readOnly={!editing}
              value={inputs.PickupTractorRecIdRef}
              onChange={onChange}
            />
          </Control>

          <Control expanded>
            <Label>Pickup Trailer</Label>
            <TrailerSelect
              icon="truck"
              name="PickupTrailerRecIdRef"
              readOnly={!editing}
              value={inputs.PickupTrailerRecIdRef}
              onChange={onChange}
            />
          </Control>
        </Field>

        <Field kind="group">
          <Control expanded>
            <Label>Actual Pickup Time In</Label>
            <DateTimePicker
              disabled={!editing}
              name="PickupTimeInFormatted"
              value={inputs.PickupTimeInFormatted}
              onChange={onChange}
            />
          </Control>
          <Control expanded>
            <Label>Actual Pickup Time Out</Label>
            <DateTimePicker
              disabled={!editing}
              name="PickupTimeOutFormatted"
              value={inputs.PickupTimeOutFormatted}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Column>
      {/* DELIVERY COLUMN */}
      <Column>
        <Field>
          <Control expanded>
            <Label>Scheduled Delivery Date/Time</Label>
            <DateTimePicker
              disabled
              readOnly
              name="ScheduledDeliveryDate"
              value={inputs.ScheduledDeliveryDateFormatted}
            />
          </Control>
        </Field>
        <Field>
          <Control>
            <Label>Delivery Driver</Label>
            <DriverSelect
              icon="user"
              name="DeliveryDriverRecIdRef"
              readOnly={!editing}
              value={inputs.DeliveryDriverRecIdRef}
              onChange={onChange}
            />
          </Control>
        </Field>
        <Field kind="group">
          <Control expanded>
            <Label>Delivery Trailer</Label>
            <TrailerSelect
              icon="truck"
              name="DeliveryTrailerRecIdRef"
              readOnly={!editing}
              value={inputs.DeliveryTrailerRecIdRef}
              onChange={onChange}
            />
          </Control>
          <Control expanded>
            <Label>Delivery Tractor</Label>
            <TractorSelect
              icon="truck"
              name="DeliveryTractorRecIdRef"
              readOnly={!editing}
              value={inputs.DeliveryTractorRecIdRef}
              onChange={onChange}
            />
          </Control>
        </Field>
        <Field kind="group">
          <Control expanded>
            <Label>Actual Delivery Time In</Label>
            <DateTimePicker
              disabled={!editing}
              name="DeliveryTimeInFormatted"
              value={inputs.DeliveryTimeInFormatted}
              onChange={onChange}
            />
          </Control>
          <Control expanded>
            <Label>Actual Delivery Time Out</Label>
            <DateTimePicker
              disabled={!editing}
              name="DeliveryTimeOutFormatted"
              value={inputs.DeliveryTimeOutFormatted}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Column>
    </Column.Group>
  </div>
);

BillOfLading.propTypes = {
  inputs: PropTypes.object,
  onChange: PropTypes.func,
  editing: PropTypes.bool,
  disabled: PropTypes.bool
};

const ElectronicLogDevice = ({ inputs, editing, onChange }) => (
  <div>
    <Column.Group>
      <Column size={6}>
        <Column.Group>
          <Column size={4}>
            <Field>
              <Control>
                <Label>Shipper Reference</Label>
                <Input
                  disabled={!editing}
                  name="ShippersNumber"
                  type="text"
                  value={inputs.ShippersNumber}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Exported To</Label>
                <DateTimePicker
                  disabled={!editing}
                  name="DateExported"
                  value={inputs.DateExported}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Imported From</Label>
                <DateTimePicker
                  disabled={!editing}
                  name="DateImported"
                  value={inputs.DateImported}
                  onChange={onChange}
                />
              </Control>
            </Field>
          </Column>
          <Column size={4}>
            <Field>
              <Control>
                <Label>Load Number</Label>
                <Input
                  disabled={!editing}
                  name="LoadNumber"
                  type="text"
                  value={inputs.LoadNumber}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Scale</Label>
                <Input
                  disabled={!editing}
                  name="Scale"
                  type="text"
                  value={inputs.Scale}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>TryCock</Label>
                <Input
                  disabled={!editing}
                  name="TryCock"
                  type="text"
                  value={inputs.TryCock}
                  onChange={onChange}
                />
              </Control>
            </Field>
          </Column>
          <Column size={4}>
            <Field>
              <Control>
                <Label>Tank ID</Label>
                <Input
                  disabled={!editing}
                  name="TankID"
                  type="text"
                  value={inputs.TankID}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>BTU</Label>
                <Input
                  disabled={!editing}
                  maxLength={4}
                  name="BTU"
                  type="text"
                  value={inputs.BTU}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Hot Flag</Label>
                <BooleanInput
                  disabled={!editing}
                  name="HotFlag"
                  value={inputs.HotFlag}
                  onChange={onChange}
                />
              </Control>
            </Field>
          </Column>
        </Column.Group>
      </Column>
      <Column size={6}>
        <Column.Group>
          {/* PICKUP COLUMN */}
          <Column>
            <Field>
              <Control>
                <Label>Pickup Inches</Label>
                <Input
                  disabled={!editing}
                  name="PickupInches"
                  type="text"
                  value={inputs.PickupInches}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Pickup Pressure</Label>
                <Input
                  disabled={!editing}
                  name="PickupPressure"
                  type="text"
                  value={inputs.PickupPressure}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Pickup Temperature</Label>
                <Input
                  disabled={!editing}
                  name="PickupTemperature"
                  type="text"
                  value={inputs.PickupTemperature}
                  onChange={onChange}
                />
              </Control>
            </Field>
          </Column>
          {/* DELIVERY COLUMN */}
          <Column>
            <Field>
              <Control>
                <Label>Delivery Inches</Label>
                <Input
                  disabled={!editing}
                  name="DeliveryInches"
                  type="text"
                  value={inputs.DeliveryInches}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Delivery Pressure</Label>
                <Input
                  disabled={!editing}
                  name="DeliveryPressure"
                  type="text"
                  value={inputs.DeliveryPressure}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Delivery Temperature</Label>
                <Input
                  disabled={!editing}
                  name="DeliveryTemperature"
                  type="text"
                  value={inputs.DeliveryTemperature}
                  onChange={onChange}
                />
              </Control>
            </Field>
          </Column>
        </Column.Group>
      </Column>
    </Column.Group>
  </div>
);
ElectronicLogDevice.propTypes = {
  inputs: PropTypes.object,
  editing: PropTypes.bool,
  onChange: PropTypes.func
};
const BolEld = ({ order, editing, disabled, onChange }) => {
  const history = useHistory();

  return (
    <div>
      <header className="bol-page-header">
        <Field>
          <Control>
            {order?.Status?.CodeDescription === "Imported from ELD" && (
              <VerifyRouteButton order={order} />
            )}
            {order?.Status?.CodeDescription !== "Imported from ELD" &&
              order?.VerifiedByRecIdRef && (
                <div style={{ display: "flex" }}>
                  <VerifiedBy verifiedByRecIdRef={order.VerifiedByRecIdRef} />
                  <Button
                    size="small"
                    style={{ marginLeft: 16 }}
                    onClick={() => history.push("/dispatch/ELD/imported")}
                  >
                    <Icon>
                      <FontAwesomeIcon icon="arrow-left" />
                    </Icon>

                    <span>Back to Imported</span>
                  </Button>
                </div>
              )}
          </Control>
        </Field>
      </header>
      <BillOfLading
        disabled={disabled}
        editing={editing}
        inputs={order}
        onChange={onChange}
      />
      <HorizontalRule />
      <ElectronicLogDevice
        editing={editing}
        inputs={order}
        onChange={onChange}
      />
    </div>
  );
};

BolEld.propTypes = {
  order: PropTypes.object,
  onChange: PropTypes.func,
  editing: PropTypes.bool,
  disabled: PropTypes.bool
};
export default BolEld;
